import React, { createContext, useCallback, useContext, useMemo, useState, useRef } from 'react';

const TabsContext = createContext(null);

export const Tabs = ({ value, defaultValue, onChange, children, className = '' }) => {
  const [internal, setInternal] = useState(defaultValue);
  const controlled = value !== undefined;
  const current = controlled ? value : internal;

  const setCurrent = useCallback(
    (next) => {
      if (!controlled) setInternal(next);
      onChange?.(next);
    },
    [controlled, onChange]
  );

  const ctx = useMemo(() => ({ current, setCurrent }), [current, setCurrent]);

  return (
    <TabsContext.Provider value={ctx}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
};

export const TabList = ({ children, className = '', ariaLabel = 'Tabs' }) => {
  const listRef = useRef(null);

  const handleKey = (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    const tabs = Array.from(listRef.current?.querySelectorAll('[role="tab"]') || []);
    const idx = tabs.indexOf(document.activeElement);
    if (idx < 0) return;
    e.preventDefault();
    let next = idx;
    if (e.key === 'ArrowLeft') next = (idx - 1 + tabs.length) % tabs.length;
    if (e.key === 'ArrowRight') next = (idx + 1) % tabs.length;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = tabs.length - 1;
    tabs[next]?.focus();
    tabs[next]?.click();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKey}
      className={`flex gap-1 border-b border-app-border overflow-x-auto ${className}`}
    >
      {children}
    </div>
  );
};

export const Tab = ({ value, children, className = '', disabled = false }) => {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('Tab deve estar dentro de <Tabs>');
  const active = ctx.current === value;

  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      disabled={disabled}
      onClick={() => ctx.setCurrent(value)}
      className={`
        px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 rounded-t
        disabled:opacity-50 disabled:cursor-not-allowed
        ${active ? 'border-primary text-primary' : 'border-transparent text-app-text-secondary hover:text-app-text'}
        ${className}
      `
        .trim()
        .replace(/\s+/g, ' ')}
    >
      {children}
    </button>
  );
};

export const TabPanels = ({ children, className = '' }) => (
  <div className={`mt-4 ${className}`}>{children}</div>
);

export const TabPanel = ({ value, children, className = '' }) => {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('TabPanel deve estar dentro de <Tabs>');
  if (ctx.current !== value) return null;
  return (
    <div role="tabpanel" className={className}>
      {children}
    </div>
  );
};

export default Tabs;
